import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'renative';
import { formatWhatsNewDate } from '@streambase/config/whats-new';
import type { WhatsNewHistory } from '@streambase/config/whats-new';

import {
  Container,
  Title,
  HistoryList,
  ReleaseCard,
  ReleaseHeader,
  VersionText,
  LatestBadge,
  LatestBadgeText,
  DateText,
  BulletRow,
  BulletDot,
  BulletText,
  BackButton,
  BackButtonText,
} from './styled';

import { withFocusable } from '@noriginmedia/react-spatial-navigation';

import whatsNew from '../../services/whats-new';
import useRemoteControl from '../../hooks/useRemoteControl';

interface SettingsViewProps {
  history: WhatsNewHistory;
  back: () => void;
  historyRef?: React.RefObject<HistoryScrollHandle>;
  onHistoryContentSizeChange?: (width: number, height: number) => void;
  onHistoryLayout?: (event: HistoryLayoutEvent) => void;
}

const HISTORY_SCROLL_STEP = 180;

interface HistoryScrollHandle {
  scrollTo: (options: { y: number; animated: boolean }) => void;
}

interface HistoryLayoutEvent {
  nativeEvent: { layout: { height: number } };
}

export function SettingsView({
  history,
  back,
  historyRef,
  onHistoryContentSizeChange,
  onHistoryLayout,
}: SettingsViewProps) {
  return (
    <Container>
      <Title aria-hidden="true" data-no-tts>
        Novidades
      </Title>
      <HistoryList
        ref={historyRef as never}
        onContentSizeChange={onHistoryContentSizeChange}
        onLayout={onHistoryLayout}
      >
        {history.releases.map((release, releaseIndex) => (
          <ReleaseCard key={release.version} $latest={releaseIndex === 0}>
            <ReleaseHeader>
              <VersionText aria-hidden="true" data-no-tts>
                Versão {release.version}
              </VersionText>
              {releaseIndex === 0 ? (
                <LatestBadge>
                  <LatestBadgeText aria-hidden="true" data-no-tts>
                    Mais recente
                  </LatestBadgeText>
                </LatestBadge>
              ) : null}
            </ReleaseHeader>
            <DateText aria-hidden="true" data-no-tts>
              {formatWhatsNewDate(release.date)}
            </DateText>
            {release.summary.map((line, summaryIndex) => (
              <BulletRow key={`${release.version}-${summaryIndex}`}>
                <BulletDot aria-hidden="true" data-no-tts>
                  •
                </BulletDot>
                <BulletText aria-hidden="true" data-no-tts>
                  {line}
                </BulletText>
              </BulletRow>
            ))}
          </ReleaseCard>
        ))}
      </HistoryList>
      <BackButton onPress={back} onEnterPress={back} focusKey={'settings-back'}>
        <BackButtonText aria-hidden="true" data-no-tts>
          Voltar
        </BackButtonText>
      </BackButton>
    </Container>
  );
}

// "Configurações / Novidades" — reads the bundled append-only history (via
// the whats-new service) and renders it locally. No network.
export default withFocusable({
  trackChildren: true,
  forgetLastFocusedChild: false,
})(function Settings(props: Record<string, unknown> & { setFocus: (key: string) => void }) {
  const navigate = useNavigate(props);
  const historyRef = useRef<HistoryScrollHandle>(null);
  const historyOffset = useRef(0);
  const historyContentHeight = useRef(0);
  const historyViewportHeight = useRef(0);

  const back = useCallback(() => {
    navigate('/catalog');
  }, [navigate]);

  const handleRemote = useCallback(
    (code: number) => {
      if (code === 38 || code === 40) {
        const direction = code === 40 ? 1 : -1;
        const maxOffset = Math.max(0, historyContentHeight.current - historyViewportHeight.current);
        const nextOffset = historyOffset.current + direction * HISTORY_SCROLL_STEP;
        historyOffset.current = Math.min(maxOffset, Math.max(0, nextOffset));
        historyRef.current?.scrollTo({ y: historyOffset.current, animated: true });
        return;
      }

      if (code === 10009 || code === 461 || code === 27 || code === 8) {
        back();
      }
    },
    [back],
  );

  useRemoteControl(handleRemote);

  const handleHistoryContentSizeChange = useCallback((_width: number, height: number) => {
    historyContentHeight.current = height;
  }, []);

  const handleHistoryLayout = useCallback((event: HistoryLayoutEvent) => {
    historyViewportHeight.current = event.nativeEvent.layout.height;
  }, []);

  useEffect(() => {
    props.setFocus('settings-back');
  }, [props.setFocus]);

  return props.path !== '/settings' ? null : (
    <SettingsView
      history={whatsNew}
      back={back}
      historyRef={historyRef}
      onHistoryContentSizeChange={handleHistoryContentSizeChange}
      onHistoryLayout={handleHistoryLayout}
    />
  );
});
